import { useEffect } from 'react';
import { getSingletonSessions } from '../api/sessions';
import { CalibrationRefType, SessionAnalysisType } from '../types/SessionAnalysisType';

interface SingletonSessionConfigs {
  DISCARDED?: string; // config_id with letter_number === 0
  DISCARDED_ARCHIVE?: string; // config_id with letter_number === 0
  FACTORY_CALIBRATION?: string; // config_id with letter_number === 0
  TEAM_CALIBRATION?: string; // config_id with letter_number === 1
  FREERIDE_UNSTRUCTURED?: string; // config_id with letter_number === 0
  FREERIDE_CALIBRATED?: string; // config_id with letter_number === 0
  lastFetched?: number;
  teamId?: string;
}

const SINGLETON_SESSIONS_KEY = 'singletonSessionIds';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Promise cache to prevent race conditions
let fetchPromise: Promise<void> | null = null;

/**
 * Fetches singleton sessions from the API and caches their config IDs in localStorage
 */
export async function fetchAndCacheSingletonSessions(teamId?: string): Promise<void> {
  try {
    const sessions = await getSingletonSessions();
    const finalTeamId = teamId || localStorage.getItem('teamId') || undefined;
    const sessionConfigs: SingletonSessionConfigs = {
      lastFetched: Date.now(),
      teamId: finalTeamId,
    };

    // Map sessions to their config IDs with letter_number === 0 based on analysis_type
    sessions.forEach((session: any) => {
      const config0 = session.configs?.find((c: any) => c.letter_number === 0);
      const config1 = session.configs?.find((c: any) => c.letter_number === 1);
      if (!config0) return;

      switch (session.analysis_type) {
        case SessionAnalysisType.DISCARDED:
          sessionConfigs.DISCARDED = config0.id;
          break;
        case SessionAnalysisType.DISCARDED_ARCHIVE:
          sessionConfigs.DISCARDED_ARCHIVE = config0.id;
          break;
        case SessionAnalysisType.REFERENCE_CALIBRATIONS:
          sessionConfigs.FACTORY_CALIBRATION = config0.id;
          sessionConfigs.TEAM_CALIBRATION = config1.id;
          break;
        case SessionAnalysisType.FREERIDE_UNSTRUCTURED:
          sessionConfigs.FREERIDE_UNSTRUCTURED = config0.id;
          break;
        case SessionAnalysisType.FREERIDE_CALIBRATED:
          sessionConfigs.FREERIDE_CALIBRATED = config0.id;
          break;
      }
    });

    localStorage.setItem(SINGLETON_SESSIONS_KEY, JSON.stringify(sessionConfigs));
  } catch (error) {
    console.error('Error fetching singleton sessions:', error);
    throw error;
  }
}

/**
 * Gets cached singleton session config IDs from localStorage
 * Refetches if cache is stale or team has changed
 */
export async function getSingletonSessionConfigs(currentTeamId?: string): Promise<SingletonSessionConfigs> {
  const cached = localStorage.getItem(SINGLETON_SESSIONS_KEY);

  if (cached) {
    const sessionConfigs: SingletonSessionConfigs = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid (not expired and same team if teamId is provided)
    const isTeamValid = !currentTeamId || sessionConfigs.teamId === currentTeamId;

    if (sessionConfigs.lastFetched && now - sessionConfigs.lastFetched < CACHE_DURATION && isTeamValid) {
      return sessionConfigs;
    }
  }

  // Cache is stale or doesn't exist, fetch new data
  // If there's already a fetch in progress, wait for it to complete
  if (fetchPromise) {
    await fetchPromise;
  } else {
    // Start a new fetch and cache the promise
    fetchPromise = fetchAndCacheSingletonSessions(currentTeamId).finally(() => {
      fetchPromise = null; // Clear the promise when done
    });
    await fetchPromise;
  }

  const newCached = localStorage.getItem(SINGLETON_SESSIONS_KEY);
  return newCached ? JSON.parse(newCached) : {};
}

/**
 * Clears the singleton sessions cache
 * Should be called on logout or team change
 */
export function clearSingletonSessionsCache(): void {
  localStorage.removeItem(SINGLETON_SESSIONS_KEY);
}

/**
 * Gets a specific singleton config ID for direct use
 * Note that calibrations are not retrieved here
 */
export async function getSingletonConfigId(analysisType: SessionAnalysisType): Promise<string> {
  const sessionConfigs = await getSingletonSessionConfigs();
  let configId: string | undefined;

  switch (analysisType) {
    case SessionAnalysisType.DISCARDED:
      configId = sessionConfigs.DISCARDED;
      break;
    case SessionAnalysisType.DISCARDED_ARCHIVE:
      configId = sessionConfigs.DISCARDED_ARCHIVE;
      break;
    case SessionAnalysisType.FREERIDE_UNSTRUCTURED:
      configId = sessionConfigs.FREERIDE_UNSTRUCTURED;
      break;
    case SessionAnalysisType.FREERIDE_CALIBRATED:
      configId = sessionConfigs.FREERIDE_CALIBRATED;
      break;
  }

  if (!configId) {
    throw new Error(`No config found for analysis type: ${analysisType}`);
  }

  return configId;
}

/**
 * Gets a specific singleton config ID for calibrations
 */
export async function getSingletonConfigCalibrationId(calibrationType: CalibrationRefType): Promise<string> {
  const sessionConfigs = await getSingletonSessionConfigs();
  let configId: string | undefined;

  switch (calibrationType) {
    case CalibrationRefType.FACTORY:
      configId = sessionConfigs.FACTORY_CALIBRATION;
      break;
    case CalibrationRefType.TEAM:
      configId = sessionConfigs.TEAM_CALIBRATION;
      break;
  }

  if (!configId) {
    fetchAndCacheSingletonSessions();
    throw new Error(`No config found for analysis type: ${calibrationType}`);
  }

  return configId;
}

/**
 * Hook to ensure singleton sessions are cached
 * Should be called early in the app lifecycle or when team changes
 */
export function useSingletonSessions(teamId?: string) {
  useEffect(() => {
    // Check cache first, only fetch if needed
    getSingletonSessionConfigs(teamId).catch((error) => {
      console.error('Failed to cache singleton sessions:', error);
    });

    // Optionally clear cache on unmount (uncomment if needed)
    // return () => {
    //   clearSingletonSessionsCache();
    // };
  }, [teamId]);
}

/**
 * Hook to refresh singleton sessions cache
 * Can be called after login or team change
 */
export function useRefreshSingletonSessions() {
  const refresh = async (teamId?: string) => {
    clearSingletonSessionsCache();
    fetchPromise = null; // Clear any pending fetch promise
    await fetchAndCacheSingletonSessions(teamId);
  };

  return refresh;
}
